import { Autocomplete, Box, Button, Paper, TextField } from "@mui/material";
import { FC, useMemo, useState } from "react";

import { ICreditTransactionFilters } from "../../types/creditTransaction";
import { IUrbanPartnerSimple } from "../../types/seederOrders";
import { useDebounce } from "../../hooks/useDebounce";
import useUrbanPartners from "../../services/useUrbanPartners";

interface TransactionFiltersProps {
  filters: ICreditTransactionFilters;
  onFiltersChange: (filters: ICreditTransactionFilters) => void;
}

const TransactionFilters: FC<TransactionFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedUrbanPartner, setSelectedUrbanPartner] =
    useState<IUrbanPartnerSimple | null>(null);
  const debouncedSearch = useDebounce(inputValue, 300);
  const { data } = useUrbanPartners({ search: debouncedSearch });
  const fetchedUrbanPartners = useMemo(
    () => data?.results || [],
    [data?.results],
  );

  const options = useMemo(() => {
    if (!selectedUrbanPartner) {
      return fetchedUrbanPartners;
    }

    const hasSelected = fetchedUrbanPartners.some(
      (up) => up.id === selectedUrbanPartner.id,
    );
    if (hasSelected) {
      return fetchedUrbanPartners;
    }

    return [selectedUrbanPartner, ...fetchedUrbanPartners];
  }, [fetchedUrbanPartners, selectedUrbanPartner]);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}
      >
        <Autocomplete
          sx={{ minWidth: 250 }}
          options={options}
          getOptionLabel={(option: IUrbanPartnerSimple) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(items) => items}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          value={selectedUrbanPartner}
          onChange={(_, newValue) => {
            setSelectedUrbanPartner(newValue);
            onFiltersChange({ ...filters, urban_partner: newValue?.id });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Parceira Urbana" size="small" />
          )}
        />
        <TextField
          label="Data Inicial"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filters.start || ""}
          onChange={(e) =>
            onFiltersChange({ ...filters, start: e.target.value })
          }
        />
        <TextField
          label="Data Final"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filters.end || ""}
          onChange={(e) => onFiltersChange({ ...filters, end: e.target.value })}
        />
        <Button
          variant="text"
          onClick={() => {
            setSelectedUrbanPartner(null);
            setInputValue("");
            onFiltersChange({});
          }}
        >
          Limpar
        </Button>
      </Box>
    </Paper>
  );
};

export default TransactionFilters;
